/**
 * Stable Manager Component
 *
 * Manages multiple horses in a stable-level view.
 * Includes ownership transfer functionality and dashboard statistics.
 *
 * @package Lovasok_Marketplace
 */

import { LovasokAPI } from '../utils/api.js';
import { formatPrice, formatNumber } from '../utils/formatters.js';

export default () => ({
    horses: [],
    loading: true,
    filterStatus: 'all',
    /** all | listing | horseface */
    filterType: 'all',
    searchQuery: '',
    sortBy: 'name',
    sortDirection: 'asc',

    // Ownership transfer
    transferModalOpen: false,
    transferHorseId: null,
    transferEmail: '',
    transferLoading: false,
    outgoingTransfers: {}, // keyed by horse_id
    incomingTransfers: [], // array of incoming transfer requests

    // Dokumentum popup
    docModalOpen: false,
    docModalHorse: null,
    docModalDocs: [],
    docModalLoading: false,
    docModalError: '',
    docUploadType: 'general',
    docUploadFile: null,
    docUploading: false,
    docUploadError: '',
    noteSavingId: null,
    noteSavedId: null,

    // Eladva popup
    soldModalOpen: false,
    soldHorse: null,
    soldReason: '',
    soldCustomReason: '',
    soldKeepHorseface: false,
    soldSubmitting: false,

    async init() {
        try {
            await Promise.all([
                this.fetchHorses(),
                this.fetchPendingTransfers(),
            ]);
        } finally {
            this.$nextTick(() => document.getElementById('lmk-stable-manager-preload')?.remove());
        }
        // Chrome néha login e-mailt tölt a keresőbe — ürítsük, ha úgy néz ki.
        this.$nextTick(() => this.clearAutofilledSearch());
        setTimeout(() => this.clearAutofilledSearch(), 300);
    },

    clearAutofilledSearch() {
        const q = String(this.searchQuery || '').trim();
        if (q && q.includes('@')) {
            this.searchQuery = '';
            const el = document.getElementById('lovasok-stable-horse-search');
            if (el) el.value = '';
        }
    },

    async fetchHorses() {
        this.loading = true;
        try {
            const data = await LovasokAPI.get('horses', {
                author: 'me',
                status: 'any',
                per_page: 100
            });
            this.horses = (data.horses || []).map(h => ({
                ...h,
                stableNotes: h.stableNotes != null ? String(h.stableNotes) : '',
                reminderCount: Number(h.reminderCount || 0),
                reminderOverdue: Number(h.reminderOverdue || 0),
            }));
        } catch (error) {
            console.error('Error fetching horses:', error);
        } finally {
            this.loading = false;
        }
    },

    async fetchPendingTransfers() {
        try {
            const data = await LovasokAPI.get('ownership/pending', { scope: 'all' });
            
            this.outgoingTransfers = {};
            this.incomingTransfers = [];
            
            (data.transfers || []).forEach(t => {
                if (t.direction === 'outgoing') {
                    this.outgoingTransfers[t.horse_id] = t;
                } else if (t.direction === 'incoming') {
                    this.incomingTransfers.push(t);
                }
            });
        } catch (error) {
            console.error('Error fetching pending transfers:', error);
        }
    },

    // Returns the pending transfer object for a horse (or null)
    getPendingTransfer(horseId) {
        return this.outgoingTransfers[horseId] || null;
    },

    isHorseface(horse) {
        return horse?.listingType === 'horseface' || horse?.listing_type === 'horseface';
    },

    // Computed: Filtered horses
    get filteredHorses() {
        let filtered = this.horses;

        if (this.filterStatus === 'sold') {
            filtered = filtered.filter(h => h.saleStatus === 'sold');
        } else if (this.filterStatus !== 'all') {
            filtered = filtered.filter(h => h.status === this.filterStatus && h.saleStatus !== 'sold');
        }

        if (this.filterType === 'horseface') {
            filtered = filtered.filter(h => this.isHorseface(h));
        } else if (this.filterType === 'listing') {
            filtered = filtered.filter(h => !this.isHorseface(h));
        }

        if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            filtered = filtered.filter(h =>
                (h.title || h.name || '').toLowerCase().includes(query) ||
                (h.breed || '').toLowerCase().includes(query)
            );
        }

        return this.sortHorses(filtered);
    },

    goToReminders() {
        this.$dispatch('lovasok-set-tab', { tab: 'reminders' });
    },

    hasReminders(horse) {
        return Number(horse?.reminderCount || 0) > 0;
    },

    hasOverdueReminders(horse) {
        return Number(horse?.reminderOverdue || 0) > 0;
    },

    listingStatsLabel(horse) {
        const views = Number(horse?.views || 0);
        const messages = Number(horse?.messages || 0);
        const parts = [`${formatNumber(views)} megtekintés`];
        if (messages > 0) {
            parts.push(messages === 1 ? '1 üzenet' : `${formatNumber(messages)} üzenet`);
        }
        return parts.join(' · ');
    },

    reminderBadgeLabel(horse) {
        const overdue = Number(horse?.reminderOverdue || 0);
        const active = Number(horse?.reminderCount || 0);
        if (overdue > 0) {
            return overdue === 1 ? '1 lejárt emlékeztető' : `${overdue} lejárt emlékeztető`;
        }
        return active === 1 ? '1 emlékeztető' : `${active} emlékeztető`;
    },

    highlightTimestamp(value) {
        if (!value) return NaN;
        return new Date(String(value).trim().replace(' ', 'T')).getTime();
    },

    highlightDaysRemaining(horse) {
        const until = this.highlightTimestamp(horse?.highlightUntil);
        if (!Number.isFinite(until)) return null;
        return Math.max(0, Math.ceil((until - Date.now()) / 86400000));
    },

    highlightRemainingLabel(horse) {
        if (horse?.untilSold && horse?.untilSoldPhase === 'kepes') return 'Amíg el nem adom';
        const days = this.highlightDaysRemaining(horse);
        if (days === null) return 'Aktív kiemelés';
        if (days === 0) return 'Ma jár le';
        return days === 1 ? '1 nap van hátra' : `${days} nap van hátra`;
    },

    highlightProgress(horse) {
        if (horse?.untilSold && horse?.untilSoldPhase === 'kepes') return 100;
        const from = this.highlightTimestamp(horse?.highlightFrom);
        const until = this.highlightTimestamp(horse?.highlightUntil);
        if (!Number.isFinite(from) || !Number.isFinite(until) || until <= from) return 100;
        const remainingRatio = (until - Date.now()) / (until - from);
        return Math.round(Math.max(0, Math.min(1, remainingRatio)) * 100);
    },

    highlightColor(horse) {
        return {
            kepes: '#fbbf24',
            normal: '#16a34a',
            premium: '#f97316',
        }[horse?.highlightType] || '#9ca3af';
    },

    async saveStableNotes(horse) {
        if (!horse?.id) return;
        const notes = String(horse.stableNotes || '');
        this.noteSavingId = horse.id;
        this.noteSavedId = null;
        try {
            const res = await LovasokAPI.post(`horses/${horse.id}/stable-notes`, { notes });
            horse.stableNotes = res?.notes != null ? String(res.notes) : notes;
            this.noteSavedId = horse.id;
            setTimeout(() => {
                if (this.noteSavedId === horse.id) this.noteSavedId = null;
            }, 1500);
        } catch (error) {
            console.error('Error saving stable notes:', error);
            alert(error?.message || 'Nem sikerült menteni a megjegyzést.');
        } finally {
            this.noteSavingId = null;
        }
    },

    // Computed: Active sale/stud/rental horses count (HorseFace kizárva)
    get activeCount() {
        return this.horses.filter(h => h.status === 'publish' && h.listingType !== 'horseface').length;
    },

    // Computed: Active HorseFace profiles count
    get activeHorsefaceCount() {
        return this.horses.filter(h => h.status === 'publish' && h.listingType === 'horseface').length;
    },

    // Computed: Total value of sale horses (HorseFace-t nem számoljuk, nincs áruk)
    get totalValue() {
        return this.horses
            .filter(h => h.listingType !== 'horseface')
            .reduce((acc, h) => acc + (parseInt(h.price) || 0), 0);
    },

    // Sort horses
    sortHorses(horses) {
        return horses.sort((a, b) => {
            // Mindig az aktívak elől
            if (a.status === 'publish' && b.status !== 'publish') return -1;
            if (a.status !== 'publish' && b.status === 'publish') return 1;

            let compareA, compareB;

            switch (this.sortBy) {
                case 'name':
                    compareA = (a.title || a.name || '').toLowerCase();
                    compareB = (b.title || b.name || '').toLowerCase();
                    break;
                case 'age':
                    compareA = parseInt(a.age) || 0;
                    compareB = parseInt(b.age) || 0;
                    break;
                case 'price':
                    compareA = parseInt(a.price) || 0;
                    compareB = parseInt(b.price) || 0;
                    break;
                default:
                    return 0;
            }

            if (this.sortDirection === 'asc') {
                return compareA > compareB ? 1 : -1;
            } else {
                return compareA < compareB ? 1 : -1;
            }
        });
    },

    // Delete single horse
    async deleteHorse(id) {
        if (!confirm('Biztosan törlöd ezt a hirdetést? A művelet nem visszavonható.')) return;

        try {
            await LovasokAPI.delete(`horses/${id}`);
            this.horses = this.horses.filter(h => h.id !== id);
        } catch (error) {
            console.error('Delete error:', error);
            alert('Hiba történt a törlés során.');
        }
    },

    // Convert HorseFace to Sale
    async convertToSale(horseId) {
        if (!confirm('Biztosan átalakítod ezt a profilt eladó ló hirdetéssé? A művelet után meg kell adnod az árat és be kell állítanod a hirdetési csomagot.')) return;
        
        try {
            const response = await LovasokAPI.post(`horses/${horseId}/convert-to-sale`, {});
            if (response.success && response.redirect_url) {
                alert(response.message || 'Sikeres átalakítás!');
                window.location.href = response.redirect_url;
            }
        } catch (error) {
            console.error('Convert to sale error:', error);
            alert('Hiba történt az átalakítás során.');
        }
    },

    async pauseHorse(horse) {
        const paidNote = horse?.isFreeListing
            ? ''
            : ' Ha van fizetett kiemelésed vagy előfizetésed, annak ideje ettől még megy.';
        if (!confirm(`Szünetelteted a(z) „${horse?.title || 'hirdetés'}” hirdetést? Lekerül a keresőből, bármikor visszakapcsolhatod.${paidNote}`)) {
            return;
        }

        try {
            await LovasokAPI.post(`horses/${horse.id}/pause`, {});
            this.horses = this.horses.map((h) => (
                h.id === horse.id ? { ...h, status: 'inactive' } : h
            ));
        } catch (error) {
            console.error('Pause listing error:', error);
            alert(error?.message || 'Nem sikerült szüneteltetni a hirdetést.');
        }
    },

    async resumeHorse(horse) {
        try {
            await LovasokAPI.post(`horses/${horse.id}/resume`, {});
            this.horses = this.horses.map((h) => (
                h.id === horse.id ? { ...h, status: 'publish' } : h
            ));
        } catch (error) {
            console.error('Resume listing error:', error);
            alert(error?.message || 'Nem sikerült visszakapcsolni a hirdetést.');
        }
    },

    // --- Eladva popup ---

    soldReasonOptions() {
        return [
            { value: 'eladta', label: 'Eladta' },
            { value: 'nem-aktualis', label: 'Nem aktuális már' },
            { value: 'megtartja', label: 'Megtartja a lovat' },
            { value: 'egyeb', label: 'Egyéb ok' },
        ];
    },

    openSoldModal(horse) {
        if (!horse || !horse.id) return;
        this.soldHorse = horse;
        this.soldReason = '';
        this.soldCustomReason = '';
        this.soldKeepHorseface = false;
        this.soldSubmitting = false;
        this.soldModalOpen = true;
    },

    closeSoldModal() {
        this.soldModalOpen = false;
        this.soldHorse = null;
        this.soldReason = '';
        this.soldCustomReason = '';
        this.soldKeepHorseface = false;
        this.soldSubmitting = false;
    },

    async submitSold() {
        if (!this.soldHorse || !this.soldHorse.id || this.soldSubmitting) return;

        if (!this.soldReason) {
            alert('Válassz okot.');
            return;
        }
        const reason = this.soldReason === 'egyeb'
            ? String(this.soldCustomReason || '').trim()
            : this.soldReason;
        if (!reason) {
            alert('Kérlek írd be az egyéb okot.');
            return;
        }

        this.soldSubmitting = true;
        const horseId = this.soldHorse.id;
        const keepHorseface = !!this.soldKeepHorseface;

        try {
            const res = await LovasokAPI.post(`horses/${horseId}/mark-sold`, {
                reason,
                keep_as_horseface: keepHorseface,
            });
            const converted = !!(res && res.converted_to_horseface);

            this.horses = this.horses.map(h => {
                if (h.id !== horseId) return h;
                if (converted) {
                    return {
                        ...h,
                        status: 'draft',
                        listingType: 'horseface',
                        listing_type: 'horseface',
                        saleStatus: 'active',
                        soldAt: null,
                    };
                }
                return { ...h, status: 'draft', saleStatus: 'sold' };
            });

            this.closeSoldModal();
            this.showDocToast(
                converted
                    ? 'Eladva — a ló HorseFace profilként megmaradt (piszkozat). Publikáláshoz HorseFace előfizetés szükséges.'
                    : 'A hirdetés eladottként jelölve.',
                'success'
            );
        } catch (error) {
            console.error('Mark sold error:', error);
            this.soldSubmitting = false;
            alert(error?.message || 'Nem sikerült menteni a státusz módosítást.');
        }
    },

    // --- Dokumentum Popup ---

    docButtonLabel(horse) {
        const n = horse?.documentCount;
        if (n == null) return 'Dokumentumok';
        if (n === 0) return 'Nincs doksi';
        return `${n} doksi`;
    },

    async openDocModal(horse) {
        this.docModalHorse = horse;
        this.docModalDocs = [];
        this.docModalError = '';
        this.docUploadFile = null;
        this.docUploadType = 'general';
        this.docUploadError = '';
        this.docModalOpen = true;
        await this.loadDocuments();
    },

    closeDocModal() {
        this.docModalOpen = false;
        this.docModalHorse = null;
        this.docModalDocs = [];
        this.docModalError = '';
        this.docUploadFile = null;
        this.docUploadError = '';
        this.docUploading = false;
    },

    async loadDocuments() {
        if (!this.docModalHorse) return;
        this.docModalLoading = true;
        this.docModalError = '';
        try {
            const data = await LovasokAPI.get(`horses/${this.docModalHorse.id}/documents`);
            this.docModalDocs = data.documents || [];
            this.docModalHorse.documentCount = this.docModalDocs.length;
            const listed = this.horses.find(h => h.id === this.docModalHorse.id);
            if (listed) listed.documentCount = this.docModalDocs.length;
        } catch (err) {
            this.docModalError = 'Nem sikerült betölteni a dokumentumokat.';
        } finally {
            this.docModalLoading = false;
        }
    },

    async deleteDocument(docId) {
        if (!this.docModalHorse) return;
        if (!confirm('Biztosan törlöd ezt a dokumentumot?')) return;

        try {
            await LovasokAPI.delete(`horses/${this.docModalHorse.id}/documents/${docId}`);
            this.docModalDocs = this.docModalDocs.filter(d => d.id !== docId);
            this.docModalHorse.documentCount = this.docModalDocs.length;
            const listed = this.horses.find(h => h.id === this.docModalHorse.id);
            if (listed) listed.documentCount = this.docModalDocs.length;
            this.showDocToast('Dokumentum törölve.', 'success');
        } catch (err) {
            this.showDocToast('Hiba a törlés során.', 'error');
        }
    },

    handleDocFileSelect(event) {
        this.docUploadFile = event.target.files[0] || null;
        this.docUploadError = '';
    },

    async uploadDocument() {
        if (!this.docUploadFile || !this.docModalHorse || this.docUploading) return;

        const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(this.docUploadFile.type)) {
            this.docUploadError = 'Csak PDF, JPG vagy PNG fájl tölthető fel.';
            return;
        }
        if (this.docUploadFile.size > 10 * 1024 * 1024) {
            this.docUploadError = 'A fájl mérete legfeljebb 10 MB lehet.';
            return;
        }

        this.docUploading = true;
        this.docUploadError = '';

        try {
            const fd = new FormData();
            fd.append('document', this.docUploadFile);
            fd.append('doc_type', this.docUploadType);

            const response = await fetch(
                `${lovasokParams.apiUrl}horses/${this.docModalHorse.id}/upload-document`,
                {
                    method: 'POST',
                    headers: { 'X-WP-Nonce': lovasokParams.nonce },
                    body: fd
                }
            );
            const result = await response.json();

            if (result && result.success) {
                this.showDocToast(`Feltöltve: ${result.doc_label}`, 'success');
                this.docUploadFile = null;
                this.docUploadType = 'general';
                // Reset the file input
                const fileInput = document.getElementById('doc-popup-file-input');
                if (fileInput) fileInput.value = '';
                await this.loadDocuments();
            } else {
                this.docUploadError = result?.message || 'Sikertelen feltöltés.';
            }
        } catch {
            this.docUploadError = 'Hálózati hiba történt.';
        } finally {
            this.docUploading = false;
        }
    },

    formatDocSize(bytes) {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    getDocTypeIcon(mime) {
        if (mime === 'application/pdf') return 'pdf';
        if (mime && mime.startsWith('image/')) return 'image';
        return 'file';
    },

    showDocToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `fixed bottom-4 right-4 z-[9999] px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all transform translate-y-2 opacity-0 ${
            type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-600'
        }`;
        toast.textContent = message;
        document.body.appendChild(toast);
        requestAnimationFrame(() => toast.classList.remove('translate-y-2', 'opacity-0'));
        setTimeout(() => {
            toast.classList.add('translate-y-2', 'opacity-0');
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    },


    // Open transfer modal
    openTransferModal(horse) {
        this.transferHorseId = horse.id;
        this.transferEmail = '';
        this.transferModalOpen = true;
    },

    // Close transfer modal
    closeTransferModal() {
        this.transferModalOpen = false;
        this.transferHorseId = null;
        this.transferEmail = '';
    },

    // Initiate ownership transfer
    async initiateTransfer() {
        if (!this.transferEmail || !this.transferHorseId) return;

        if (!confirm(`Biztosan átadod ezt a lovat ${this.transferEmail} felhasználónak?`)) return;

        this.transferLoading = true;

        try {
            await LovasokAPI.post('ownership/initiate', {
                horse_id: this.transferHorseId,
                email: this.transferEmail,
            });

            alert('Tulajdonosváltási kérelem elküldve! Az új tulajdonos emailben fog értesítést kapni.');
            this.closeTransferModal();
            await this.fetchPendingTransfers();

        } catch (error) {
            console.error('Transfer error:', error);
            alert('Hiba történt a tulajdonosváltás során.');
        } finally {
            this.transferLoading = false;
        }
    },

    // Cancel a pending transfer
    async cancelTransfer(horseId) {
        const transfer = this.getPendingTransfer(horseId);
        if (!transfer) return;

        if (!confirm(`Biztosan visszavonod a tulajdonosváltási kérelmet? (${transfer.new_owner_email})`)) return;

        try {
            await LovasokAPI.post('ownership/cancel', {
                transfer_id: transfer.id,
            });

            alert('Tulajdonosváltási kérelem visszavonva. Az érintett fél értesítést kap.');
            delete this.outgoingTransfers[horseId];
            this.outgoingTransfers = { ...this.outgoingTransfers }; // trigger reactivity

        } catch (error) {
            console.error('Cancel transfer error:', error);
            alert('Hiba történt a visszavonás során.');
        }
    },

    // Accept an incoming transfer request
    async acceptIncomingTransfer(transferId) {
        if (!confirm('Biztosan elfogadod a tulajdonosváltást? Sikeres elfogadás esetén a ló a te tulajdonodba kerül.')) return;

        try {
            await LovasokAPI.post('ownership/accept-incoming', {
                transfer_id: transferId,
            });

            alert('Tulajdonosváltás sikeresen elfogadva!');
            await Promise.all([
                this.fetchPendingTransfers(),
                this.fetchHorses() // reload horses since we now own a new one
            ]);
        } catch (error) {
            console.error('Accept transfer error:', error);
            alert('Hiba történt az elfogadás során.');
        }
    },

    // Reject an incoming transfer request
    async rejectIncomingTransfer(transferId) {
        if (!confirm('Biztosan visszautasítod a tulajdonosváltást?')) return;

        try {
            await LovasokAPI.post('ownership/reject-incoming', {
                transfer_id: transferId,
            });

            alert('Tulajdonosváltás visszautasítva.');
            await this.fetchPendingTransfers();
        } catch (error) {
            console.error('Reject transfer error:', error);
            alert('Hiba történt a visszautasítás során.');
        }
    },

    // Format price
    formatPrice(price, priceStatus = '', priceCategory = '', currency = 'Ft') {
        return formatPrice(price, true, priceStatus, priceCategory, currency);
    },

    // Format compact number (1.2M, 500K)
    formatCompactNumber(number) {
        const n = Number(number) || 0;
        if (n >= 1000000) {
            return (n / 1000000).toFixed(1) + 'M Ft';
        }
        if (n >= 1000) {
            return (n / 1000).toFixed(0) + 'K Ft';
        }
        return formatNumber(n) + ' Ft';
    },

    /**
     * CSV export of the current filtered list (Excel-friendly: UTF-8 BOM, semicolon).
     */
    exportStableList() {
        const list = Array.isArray(this.filteredHorses) && this.filteredHorses.length
            ? this.filteredHorses
            : this.horses;

        const statusLabel = (h) => {
            if (h.saleStatus === 'sold') return 'Eladva';
            const m = { publish: 'Aktív', inactive: 'Szüneteltetve', draft: 'Vázlat', pending: 'Függőben' };
            return m[h.status] || (h.status || '');
        };

        const esc = (v) => {
            const s = String(v ?? '');
            if (/[";\r\n]/.test(s)) {
                return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
        };

        const header = ['ID', 'Név', 'Fajta', 'Státusz', 'Ár (Ft)', 'Életkor', 'URL'];
        const lines = [header.join(';')];

        for (const proxyObj of list) {
            // Un-proxy the object just in case Alpine makes it hard to iterate
            const h = Object.assign({}, proxyObj);
            const rowUrl = h.link || '';
            lines.push(
                [
                    h.id,
                    h.title || h.name || '',
                    h.breed || '',
                    statusLabel(h),
                    h.price != null && h.price !== '' ? String(h.price) : '',
                    h.age != null && h.age !== '' ? String(h.age) : '',
                    rowUrl
                ].map(esc).join(';')
            );
        }

        const bom = '\uFEFF';
        const blob = new Blob([bom + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
        
        // Use a more robust download method
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, `menes-export-${new Date().toISOString().slice(0, 10)}.csv`);
            return;
        }
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        const day = new Date().toISOString().slice(0, 10);
        a.download = `menes-export-${day}.csv`;
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    }
});
